<template>
  <div class="w-full">
    <div class="rounded-2xl border border-base-300 bg-base-100 p-4 shadow-sm">
      <div class="flex items-start justify-between">
        <div>
          <div class="text-sm font-semibold" :class="isRtl === true ? 'text-right' : 'text-left'">{{ t('aiPhoneIvrSetup.routes.title', 'Division Routes') }}</div>
          <div class="mt-1 text-xs text-base-content opacity-70" :class="isRtl === true ? 'text-right' : 'text-left'">{{ t('aiPhoneIvrSetup.routes.description', 'Add the divisions this IVR can transfer to and define how each route should behave.') }}</div>
        </div>
        <button type="button" class="btn btn-sm" @click="addRoute()">{{ t('aiPhoneIvrSetup.routes.add', 'Add Division') }}</button>
      </div>
      <div v-if="routes.length === 0" class="mt-4 rounded-xl border border-dashed border-base-300 bg-base-200/40 px-4 py-6 text-sm text-base-content opacity-70" :class="isRtl === true ? 'text-right' : 'text-left'">
        {{ t('aiPhoneIvrSetup.routes.empty', 'No divisions added yet. Start by adding the first transfer route.') }}
      </div>
      <div v-else class="mt-4">
        <div v-for="(routeItem, index) in routes" :key="routeItem.id" class="rounded-2xl border border-base-300 bg-base-50 p-4" :style="index > 0 ? 'margin-top: 16px;' : ''">
          <div class="flex items-center justify-between">
            <div class="text-sm font-semibold">{{ t('aiPhoneIvrSetup.routes.cardTitle', 'Division') }} {{ index + 1 }}</div>
            <button type="button" class="btn btn-sm btn-ghost text-error" @click="removeRoute(index)">{{ t('aiPhoneIvrSetup.routes.remove', 'Remove') }}</button>
          </div>
          <div class="mt-4">
            <label class="label">
              <span class="label-text">{{ t('aiPhoneIvrSetup.routes.divisionName', 'Division Name') }}</span>
            </label>
            <input type="text" class="input input-bordered w-full" :value="routeItem.divisionName" @input="updateRoute(index, 'divisionName', $event.target.value)" />
          </div>
          <div class="mt-4">
            <label class="label">
              <span class="label-text">{{ t('aiPhoneIvrSetup.routes.transferDescription', 'When to Transfer to This Division') }}</span>
            </label>
            <textarea class="textarea textarea-bordered w-full" style="min-height: 96px;" :value="routeItem.transferDescription" @input="updateRoute(index, 'transferDescription', $event.target.value)"></textarea>
          </div>
          <div class="mt-4">
            <label class="label">
              <span class="label-text">{{ t('aiPhoneIvrSetup.routes.transferSentence', 'Sentence Before Transfer') }}</span>
            </label>
            <textarea class="textarea textarea-bordered w-full" style="min-height: 84px;" :value="routeItem.transferSentence" @input="updateRoute(index, 'transferSentence', $event.target.value)"></textarea>
          </div>
          <div class="mt-4 flex flex-wrap">
            <div class="w-full md:w-1/3 md:pr-2">
              <label class="label">
                <span class="label-text">{{ t('aiPhoneIvrSetup.routes.sipPort', 'SIP Port') }}</span>
              </label>
              <input type="text" class="input input-bordered w-full" :value="routeItem.sipPort" @input="updateRoute(index, 'sipPort', $event.target.value)" />
            </div>
            <div class="w-full md:w-1/3 md:px-2">
              <label class="label">
                <span class="label-text">{{ t('aiPhoneIvrSetup.routes.ip', 'IP') }}</span>
              </label>
              <input type="text" class="input input-bordered w-full" :value="routeItem.ip" @input="updateRoute(index, 'ip', $event.target.value)" />
            </div>
            <div class="w-full md:w-1/3 md:pl-2">
              <label class="label">
                <span class="label-text">{{ t('aiPhoneIvrSetup.routes.phoneNumber', 'Phone Number') }}</span>
              </label>
              <input type="text" class="input input-bordered w-full" :value="routeItem.phoneNumber" @input="updateRoute(index, 'phoneNumber', $event.target.value)" />
              <div v-if="getIsraeliPhoneError(routeItem.phoneNumber) !== ''" class="mt-1 text-xs text-error">{{ getIsraeliPhoneError(routeItem.phoneNumber) }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from '@/i18n';

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => []
  }
});

const emit = defineEmits(['update:modelValue']);

const { t, currentLanguage } = useI18n();
const isRtl = computed(() => ['he', 'ar'].includes(currentLanguage.value) === true);

const routes = computed(() => {
  if (Array.isArray(props.modelValue) !== true) {
    return [];
  }
  return props.modelValue.map((item) => normalizeRoute(item));
});

function createRouteId() {
  return `${Date.now()}-${Math.floor(Math.random() * 1000000)}`;
}

function createEmptyRoute() {
  return {
    id: createRouteId(),
    divisionName: '',
    transferDescription: '',
    transferSentence: '',
    sipPort: '',
    ip: '',
    phoneNumber: ''
  };
}

function normalizeRoute(item) {
  const source = item !== null && item !== undefined && typeof item === 'object' ? item : {};
  return {
    id: typeof source.id === 'string' && source.id.trim() !== '' ? source.id : createRouteId(),
    divisionName: typeof source.divisionName === 'string' ? source.divisionName : '',
    transferDescription: typeof source.transferDescription === 'string' ? source.transferDescription : '',
    transferSentence: typeof source.transferSentence === 'string' ? source.transferSentence : '',
    sipPort: typeof source.sipPort === 'string' ? source.sipPort : String(source.sipPort !== undefined && source.sipPort !== null ? source.sipPort : ''),
    ip: typeof source.ip === 'string' ? source.ip : '',
    phoneNumber: typeof source.phoneNumber === 'string' ? source.phoneNumber : ''
  };
}

function emitRoutes(nextRoutes) {
  emit('update:modelValue', nextRoutes.map((item) => normalizeRoute(item)));
}

function addRoute() {
  const nextRoutes = routes.value.slice();
  nextRoutes.push(createEmptyRoute());
  emitRoutes(nextRoutes);
}

function removeRoute(index) {
  const nextRoutes = routes.value.slice();
  if (index >= 0 && index < nextRoutes.length) {
    nextRoutes.splice(index, 1);
    emitRoutes(nextRoutes);
  }
}

function updateRoute(index, key, value) {
  const nextRoutes = routes.value.slice();
  if (index < 0 || index >= nextRoutes.length) {
    return;
  }
  const currentRoute = normalizeRoute(nextRoutes[index]);
  currentRoute[key] = typeof value === 'string' ? value : '';
  nextRoutes[index] = currentRoute;
  emitRoutes(nextRoutes);
}

function getIsraeliPhoneError(value) {
  const rawValue = typeof value === 'string' ? value.trim() : '';
  if (rawValue === '') {
    return '';
  }
  const compactValue = rawValue.replace(/\s+/g, '');
  const digitsOnly = compactValue.replace(/[^\d]/g, '');
  if (compactValue.startsWith('+972') === true || compactValue.startsWith('972') === true) {
    return t('aiPhoneIvrSetup.routes.israeliNumberError', 'Israeli phone numbers must start with 0.');
  }
  if (digitsOnly.length === 9 && compactValue.startsWith('0') !== true) {
    return t('aiPhoneIvrSetup.routes.israeliNumberError', 'Israeli phone numbers must start with 0.');
  }
  return '';
}
</script>
